// Verbindet die Guards aus hooks.js mit dem Limit-Dialog. Aufrufer prüfen
// vor dem Öffnen eines Modals oder vor der Mutation und brechen bei false ab.
// Nicht durchgesetzt oder ein Zählfehler heißt freigeben (fail-open), ein
// Block öffnet den Limit-Dialog mit Nutzung und Optionen.
import { useCallback, useMemo } from 'react'

import { useCareerGate, useSeatGuard, useTalentRecoverGuard } from './hooks'
import {
  LIMIT_DIALOG_ACTIONS,
  usePricingDialog
} from './pricing-dialog-context'

// count ist ohne Angabe das delta, also die gebrauchten Plätze
const openIfBlocked = ({
  result,
  resource,
  action,
  source,
  openLimitDialog,
  count = result?.delta
}) => {
  if (!result || result.allowed) return true

  openLimitDialog(resource, {
    source,
    action,
    count: typeof count === 'number' ? count : undefined,
    used: typeof result.used === 'number' ? result.used : undefined,
    limit: typeof result.limit === 'number' ? result.limit : undefined
  })
  return false
}

/**
 * @returns {{
 *   guardCareerActivation(careerOrCareers, { source, action? }): Promise<boolean>,
 *   guardCareerRecover(careers, { source }): Promise<boolean>,
 *   guardSeatInvite({ source }): Promise<boolean>,
 *   guardTalentRecover(spaces, { source }): Promise<boolean>
 * }}
 */
export const useLimitGuards = () => {
  const { checkActivation, checkRecover } = useCareerGate()
  const { checkInvite } = useSeatGuard()
  const { checkRecover: checkTalentRecover } = useTalentRecoverGuard()
  const { openLimitDialog } = usePricingDialog()

  const guardCareerActivation = useCallback(
    async (careerOrCareers, { source, action } = {}) =>
      openIfBlocked({
        result: await checkActivation(careerOrCareers),
        resource: 'careers',
        action: action || LIMIT_DIALOG_ACTIONS.ACTIVATE,
        source,
        openLimitDialog
      }),
    [checkActivation, openLimitDialog]
  )

  const guardCareerRecover = useCallback(
    async (careers, { source } = {}) =>
      openIfBlocked({
        result: await checkRecover(careers),
        resource: 'careers',
        action: LIMIT_DIALOG_ACTIONS.RECOVER,
        source,
        openLimitDialog
      }),
    [checkRecover, openLimitDialog]
  )

  const guardSeatInvite = useCallback(
    async ({ source } = {}) =>
      openIfBlocked({
        result: await checkInvite(),
        resource: 'seats',
        action: LIMIT_DIALOG_ACTIONS.INVITE,
        source,
        openLimitDialog
      }),
    [checkInvite, openLimitDialog]
  )

  // Der Text nennt die ausgewählten Talente, nicht die gebrauchten Plätze.
  // Gezählt wird je Person, Zeilen einer schon gezählten Person brauchen
  // keinen Platz, das delta wäre dann kleiner als die Auswahl.
  const guardTalentRecover = useCallback(
    async (spaces, { source } = {}) =>
      openIfBlocked({
        result: await checkTalentRecover(spaces),
        resource: 'talentStorage',
        action: LIMIT_DIALOG_ACTIONS.RECOVER,
        source,
        openLimitDialog,
        count: Array.isArray(spaces) ? spaces.length : spaces ? 1 : 0
      }),
    [checkTalentRecover, openLimitDialog]
  )

  return useMemo(
    () => ({
      guardCareerActivation,
      guardCareerRecover,
      guardSeatInvite,
      guardTalentRecover
    }),
    [
      guardCareerActivation,
      guardCareerRecover,
      guardSeatInvite,
      guardTalentRecover
    ]
  )
}
